/**
 * Una regla en una línea.
 *
 * *** Para qué. *** Cinco filas de "Manual · Completada · 32" son indistinguibles entre sí.
 * Lo que identifica una corrida es lo que hizo: `+12% en Relojería, redondeo 900`. Esta
 * función arma ese texto y es la única que lo arma, así que el historial, el detalle y —el
 * día que existan— los mails de aviso dicen exactamente lo mismo.
 *
 * Vive en el motor y no en la app porque describe los conceptos del motor. Si mañana se
 * agrega un tipo de ajuste, el `switch` de acá es exhaustivo y el typecheck señala el lugar
 * que falta completar. En la app no habría nada que lo obligue, y encima no hay tests ahí.
 *
 * *** Tiene subcamino propio en `package.json` (`@precios/rules/describir`). *** La app
 * embebida lo importa por ahí y no por el barrel: `index.js` reexporta `schema.js`, que
 * arrastra zod, y en un bundle de navegador eso se paga en kilobytes. Este módulo depende
 * sólo de `money.js`; todo lo demás son tipos, que se borran al compilar.
 *
 * *** Los nombres de categorías y marcas son opcionales a propósito. *** La regla guarda
 * ids de categoría y claves de marca, no nombres: resolverlos cuesta una llamada a la API
 * de Tienda Nube, y el rate limit de esa tienda es compartido con todas las apps que el
 * comerciante tenga instaladas. Así que quien tenga los nombres a mano los pasa, y quien no
 * los tenga obtiene "en 3 categorías", que es peor pero es cierto. Nunca un id crudo: al
 * comerciante `40433873` no le dice nada.
 */
import { pesos } from './money.js';
const NUMERO = new Intl.NumberFormat('es-AR');
/**
 * La regla completa en una línea, con las partes separadas por comas.
 *
 * El orden es el de la pregunta que se hace el comerciante: cuánto, sobre qué, con qué
 * redondeo. El campo afectado va al final y sólo cuando no es el precio de lista, que es
 * el caso normal.
 */
export function describirRegla(spec, nombres = {}) {
    // El ajuste y el alcance van pegados con un espacio y no con una coma: "+12% en
    // Relojería" es una frase, "+12%, en Relojería" son dos cosas sueltas. Las demás partes
    // sí son agregados y van separadas.
    const encabezado = `${describirAjuste(spec.adjustment)} ${describirAlcance(spec.scope, nombres)}`;
    const agregados = [describirRedondeo(spec.rounding), describirCampos(spec.targetFields)];
    return [encabezado, ...agregados.filter((parte) => parte !== null)].join(', ');
}
/** "+12%", "-$ 1.500", "precio fijo $ 9.900", "margen 50% sobre el costo". */
export function describirAjuste(ajuste) {
    switch (ajuste.kind) {
        case 'percentage':
            return `${conSigno(ajuste.percent)}%`;
        case 'fixed_amount':
            // El importe va sin signo y el signo adelante: "-$ 1.500", no "$ -1.500".
            return `${ajuste.amount < 0 ? '-' : '+'}${pesos(Math.abs(ajuste.amount))}`;
        case 'set_price':
            return `precio fijo ${pesos(ajuste.amount)}`;
        case 'target_margin':
            return `margen ${String(ajuste.percent)}% sobre ${ajuste.basis === 'markup' ? 'el costo' : 'la venta'}`;
    }
}
/**
 * "en todo el catálogo", "en Relojería", "en Relojería y Joyería", "en 3 categorías",
 * "en Nike", "de $ 1.000 a $ 5.000".
 *
 * Cuando hay varios criterios se enumeran todos: una regla acotada por categoría **y** por
 * rango de precios no es la misma que una acotada sólo por categoría, y confundirlas en el
 * historial es peor que un texto largo.
 */
export function describirAlcance(alcance, nombres = {}) {
    if (alcance.allProducts)
        return 'en todo el catálogo';
    const criterios = [];
    if (alcance.categoryIds.length > 0) {
        criterios.push(enumerar(alcance.categoryIds.map((id) => nombres.categorias?.get(id)), alcance.categoryIds.length, 'categoría', 'categorías'));
    }
    if (alcance.brands.length > 0) {
        criterios.push(enumerar(alcance.brands.map((clave) => nombres.marcas?.get(clave) ?? conMayuscula(clave)), alcance.brands.length, 'marca', 'marcas'));
    }
    const rango = describirRango(alcance.priceMin, alcance.priceMax);
    if (rango !== null)
        criterios.push(rango);
    // Un alcance sin ningún criterio no pasa el esquema, así que esto no debería pasar. Si
    // pasa, mejor decir que no sabemos que afirmar que era todo el catálogo.
    if (criterios.length === 0)
        return 'sin alcance definido';
    return `en ${criterios.join(' y ')}`;
}
/** "redondeo 900", "redondeo a $ 100", o `null` cuando no hay redondeo. */
export function describirRedondeo(redondeo) {
    switch (redondeo.kind) {
        case 'none':
            return null;
        case 'ending':
            return `redondeo ${redondeo.ending}`;
        case 'multiple':
            // `multiple` está en pesos, no en unidades mínimas: el motor lo multiplica por
            // `minorUnit(decimals)` al compilar la congruencia. Pasarlo por `pesos()` acá lo
            // dividiría por cien y diría "$ 1" donde va "$ 100".
            return `redondeo a $ ${NUMERO.format(redondeo.multiple)}`;
    }
}
/**
 * "sólo ofertas", "precio y oferta", o `null` para el precio de lista.
 *
 * El precio de lista es el caso normal y nombrarlo en cada fila sería ruido. Los otros dos
 * sí cambian lo que pasó en la góndola, así que se dicen siempre.
 */
export function describirCampos(campos) {
    switch (campos) {
        case 'price':
            return null;
        case 'promotional_price':
            return 'sólo ofertas';
        case 'both':
            return 'precio y oferta';
    }
}
// ---------------------------------------------------------------------------
function conSigno(numero) {
    return numero > 0 ? `+${String(numero)}` : String(numero);
}
/** "casio" → "Casio". Es lo mejor que se puede hacer sin los nombres de verdad. */
function conMayuscula(texto) {
    if (texto === '')
        return texto;
    return texto[0].toLocaleUpperCase('es-AR') + texto.slice(1);
}
/**
 * Enumera hasta dos nombres; de tres en adelante, cuenta.
 *
 * "en Relojería y Joyería" se lee de un vistazo; "en Relojería, Joyería, Bijouterie y
 * Accesorios" ocupa toda la fila y tapa el resto de la regla. Y si falta el nombre de
 * alguno, cuenta también: media enumeración con un hueco es peor que un número.
 */
function enumerar(nombres, cantidad, singular, plural) {
    const conocidos = nombres.filter((nombre) => nombre !== undefined);
    if (conocidos.length === cantidad && cantidad <= 2)
        return conocidos.join(' y ');
    return `${String(cantidad)} ${cantidad === 1 ? singular : plural}`;
}
/** "de $ 1.000 a $ 5.000", "desde $ 1.000", "hasta $ 5.000". */
function describirRango(minimo, maximo) {
    if (minimo !== null && maximo !== null)
        return `de ${pesos(minimo)} a ${pesos(maximo)}`;
    if (minimo !== null)
        return `desde ${pesos(minimo)}`;
    if (maximo !== null)
        return `hasta ${pesos(maximo)}`;
    return null;
}
//# sourceMappingURL=describir.js.map